package org.multiplatform.kotfolio.pages.compose.side_effect

import androidx.compose.runtime.Composable
import androidx.compose.runtime.SideEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import org.multiplatform.kotfolio.components.KotlinCode
import org.multiplatform.kotfolio.components.WebImage
import org.multiplatform.kotfolio.layout.BlogLayout
import org.multiplatform.kotfolio.layout.BlogProperties
import org.multiplatform.kotfolio.layout.PageProperties
import org.multiplatform.kotfolio.util.Resources
import com.varabyte.kobweb.compose.css.FontSize
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ol
import org.jetbrains.compose.web.dom.Ul

@Page("/compose/side_effect/side_effect")
@Composable
fun SideEffectPage() {
    BlogLayout(
        PageProperties(
            "Side Effect",
            ""
        ),
        BlogProperties("Jetpack Compose", "Side Effect"))
    {
        Column(
            horizontalAlignment = Alignment.Start
        ) {
            SpanText(
                text = "Why do we need Side Effects?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ol {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "To run code once when a screen enters composition.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "To run code whenever a key changes.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "To start/stop coroutines tied to the composable lifecycle.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "To read non-stable external objects (e.g., SharedPreferences).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "To update the state safely from background threads.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "What are Side-Effects?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Side effects in Composable refer to performing operations that aren't part of the Ul itself but are triggered by changes in the state or lifecycle events.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A side-effect is any change to the app that happens outside of a composable function's control (A side effect is Anything that escapes the scope of the function where it is called). Because composable functions can re-run at unexpected times, in different sequences, or get canceled, it's better to avoid having them cause changes outside their scope. This keeps things more predictable and prevents issues.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Modifies state outside the current composable.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Communicates with external systems (network, database, sensors, etc.)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Launches coroutines or subscriptions.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Needs to be aware of the composable’s lifecycle (when it enters or leaves the composition).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Why Side-effects?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A side effect is any change in the app's state that occurs outside of a composable function’s direct control (A side effect is anything that escapes the scope of your composable and interacts with the outside world). In Jetpack Compose, composable functions can be re-executed frequently and unpredictably due to recomposition triggered by state changes, parameter updates, or other events. Therefore, you can’t assume that a composable will be invoked only once. The purpose of side effects in Jetpack Compose is to allow for the execution of non-UI related operations that change the state of the app outside of a Composable function in a controlled and predictable manner.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A side-effect is a change to the state of the app that happens outside the scope of a composable function or perform asynchronous operation. For example to trigger Snackbar, these action should be called from a controlled environment that is aware of the lifecycle of the composable.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Side effects, such as updating a database or making a network call, should be kept separate from the UI rendering logic to improve the performance and maintainability of the code.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In Compose, making network calls, collecting flows, or registering sensors are all side effects.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Jetpack Compose provides several Composable functions, such as SideEffect, LaunchedEffect, and DisposableEffect, that enable developers to manage side effects effectively, by decoupling them from the UI rendering logic and executing them in a separate coroutine scope.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            WebImage(
                src = Resources.Images.SIDE_EFFECT_1,
                modifier = Modifier.fillMaxWidth().padding(5.px).borderRadius(10.px),
                contentDescription = "Side Effect"
            )

            WebImage(
                src = Resources.Images.SIDE_EFFECT_2,
                modifier = Modifier.fillMaxWidth().padding(5.px).borderRadius(10.px),
                contentDescription = "Side Effect"
            )

            WebImage(
                src = Resources.Images.SIDE_EFFECT_3,
                modifier = Modifier.fillMaxWidth().padding(5.px).borderRadius(10.px),
                contentDescription = "Side Effect"
            )

            SpanText(
                text = "SideEffect - (Update External Systems After UI Changes - For Running on Every Recomposition - Synchronizing State with External Systems - Sharing State with Non-Compose Code)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            WebImage(
                src = Resources.Images.SIDE_EFFECT_4,
                modifier = Modifier.fillMaxWidth().padding(5.px).borderRadius(10.px),
                contentDescription = "Side Effect"
            )

            Ul {
                SpanText(
                    text = "Side effects must be executed at the right time. First, a Composable life cycle needs to be specified:",
                    modifier = Modifier
                        .fontSize(FontSize.Medium)
                )

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "onActive (or onEnter) : when the Composable first enters the component tree.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "onCommit (or onUpdate) : When the UI is updated with recomposition.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "onDispose (or onLeave) : when the Composable is removed from the component tree.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Executes after composition completes. Used to sync Compose state with non-Compose code, , like analytics or logs.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }


                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Composables can recompose multiple times. If side effects are placed directly, they may repeat unexpectedly.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "SideEffect is used to send state updates to parts of the app that are outside the Compose lifecycle, such as ViewModels or logs. It runs on every recomposition.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Running non-suspend functions in every recomposition. SideEffect is a function in jetpack compose that is used to do side work without affecting the UI performance. SideEffect is used to synchronize Compose state with external systems that are not managed by Compose. This guarantees that the side-effect runs after every successful recomposition.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The code that will run outside the scope of any composable function is called side-effects. Side-effect is anything happening out of the scope of a composable function that eventually affects the composable function, it could be some state changes or anything happening on UI like user actions which has an effect on Composable. Both APIs are built to handle that effect in a controlled environment.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Compose state with objects not managed by the Compose, use the SideEffect composable, as it's invoked on every successful recomposition.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Never run any non-composable code inside composable function, always use side-effect for that. It ensures that code inside it will always run after the Composable function has finished rendering.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A side-effect t is a change in app state that occurs outside the Compose function. As mentioned before, composable functions should be free of side effects. In other words, in order to change compose, only the form that is called again through the changed param must exist, and it must not be configured to affect external variables or operations inside the compose function. (This is because recompose can occur frequently due to the lifecycle of compose and can be called from multiple threads.)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "However, since there may be cases where the external state needs to change due to the internal operation of Compose, in this case, it should be implemented in a form that recognizes the lifecycle of Compose and operates accordingly.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Therefore, in order to support this, a composable function that handles side-effects when composition is completed is supported. These composable functions are defined by the word effect, and an effect is a composable function that does not emit UI.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Responsive UI is asynchronous by default. Compose does not use callback for this process, but wraps it in corouitnes at the API level.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "To share Compose state with objects not managed by compose, use the SideEffect composable, as it’s invoked on every successful recomposition.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "SideEffect can be used to update the external state. When a function changes a variable, value, or object outside its scope. As follows, when the drawerState changes, the external TouchHandler is notified. Since TouchHandler is the only single instance of the process, there is no need to consider the issue of leakage.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "SideEffect is basically, a kind of LaunchedEffect. It launches in every recomposition and saves us from creating a state for LaunchedEffect but it can’t run suspend functions directly.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Toast: UI is declarative, but showing a Toast is a side effect—it affects the outside world.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Logging and Analytics: Your analytics library might allow you to segment your user population by attaching custom metadata (user properties in this example) to all subsequent analytics events.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Performing One-Time Initialization such as setting up a connection to a Bluetooth device, loading data from a file, or initializing a library.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "You want to perform some actions that are related to the UI but don't directly affect its state.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Managing state or lifecycle events.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Publishing state changes to external systems.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Updating non-Compose objects with Compose state",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Synchronizing with external state holders (keeping external objects in sync)",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                KotlinCode(
                    code = """
                        //kobweb navigation scroll
                        
                        @Composable
                        fun NavHeader() {
                            var activeSection by remember { mutableStateOf("home") }
                            // Side effect to listen to scroll events and update active section
                            SideEffect {
                                window.onscroll = {
                                    val sections = listOf("home", "about", "experience", "projects", "contact")
                                    sections.forEach { section ->
                                        val element = document.getElementById(section)
                                        if (element != null) {
                                            val rect = element.getBoundingClientRect()
                                            if (rect.top <= window.innerHeight / 2 && rect.bottom >= window.innerHeight / 2) {
                                                activeSection = section
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    """.trimIndent()
                )

                KotlinCode(
                    code = $$"""
                           var themeMode by remember { mutableStateOf(ThemeMode.SYSTEM) }
    
                            AppTheme(themeMode) { isDarkTheme ->
                                SideEffect {
                                    onThemeChanged(isDarkTheme)
                                }
                                
                                var selectedTabIndex by remember { mutableStateOf(0) }
      
                                // Build TradingView widget URL with theme parameter based on current theme selection
                                val currentUrl = remember(selectedTabIndex, isDarkTheme) {
                                    val themeParam = if (isDarkTheme) "dark" else "light"
                                    "https://www.tradingview.com/widgetembed/?symbol=${tabs[selectedTabIndex].symbol}&theme=$themeParam"
                                }
                            }    
                    """.trimIndent()
                )

                KotlinCode(
                    code = """
                        SideEffect {
                            //Log, println

                            object Logger {
                                var user: String = "UNKNOWN"

                                fun write(message: String) {
                                    Log.v(message)
                                    Log.v(user)
                                }
                            }

                            @Composable
                            fun rememberLogger(userName: String): Logger {
                                val logger: Logger = remember { Logger }
                                SideEffect {
                                    logger.user = userName
                                }
                                return logger
                            }
                        }
                    """.trimIndent()
                )

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "SideEffect is a composable function that allows you to execute side effects when the parent composable function recomposes. SideEffect is useful when you want to perform operations that do not depend on the state or props of the composable.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "When a composable function is recomposed, the code inside the composable function is re-executed (including side effects, of course). However, the UI is only updated where the state/props of the composable are used.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                KotlinCode(
                    code = """
                    @Composable
                    fun Counter() {
                        // Define a state variable for the count
                        val count = remember { mutableStateOf(0) }

                        // Use SideEffect to log the current value of count
                        SideEffect {
                            // Called on every recomposition
                            log(count.value)
                        }

                        Column {
                            Button(onClick = { count.value++ }) {
                                // This recomposition doesn't trigger the outer side effect
                                // every time button has been tapped
                                Text(count.value)
                            }
                        }
                    }
                    """.trimIndent()
                )

                KotlinCode(
                    code = """
                        @Composable
                        fun Counter() {
                            // Define a state variable for the count
                            val count = remember { mutableStateOf(0) }

                            // Use SideEffect to log the current value of count
                            SideEffect {
                                // Called on every recomposition
                                log(count.value) //Outer Count
                            }

                            Column {
                                Button(onClick = { count.value++ }) {
                                    // Use SideEffect to log the current value of count
                                    SideEffect {
                                        // Called on every recomposition
                                        log(count.value) //Inner Count
                                    }

                                    // This recomposition doesn't trigger the outer side effect
                                    // every time button has been tapped
                                    Text("Increase Count {count.value}")
                                }
                            }
                        }
                    """.trimIndent()
                )

                KotlinCode(
                    code = $$"""
                        @Composable
                        fun SideEffectScreen() {
                            var sideEffectState by remember { mutableStateOf(true) }
                            val analytics = rememberAnalytics()

                            if (sideEffectState) analytics.setUserProperty("key1", "value1")

                            Button(onClick = {
                                sideEffectState = !sideEffectState
                            }) {
                                Text(text = "Click Here")
                            }
                        }

                        @Composable
                        fun rememberAnalytics(): Analytics {
                            val analytics = remember { Analytics() }

                            SideEffect {
                                logger { "SideEffect !!!" }
                                analytics.setUserProperty("userKey", "type")
                            }

                            return analytics
                        }

                        class Analytics {
                            fun setUserProperty(key: String, value: String) {
                                logger { "key: $key :: value: $value" }
                            }
                        }
                    """.trimIndent()
                )

                KotlinCode(
                    code = """
                        @Composable
                        fun SetStatusBarColor(
                            navController: NavHostController
                        ){

                            val backStackEntry by navController.currentBackStackEntryAsState()
                            val systemUiController = rememberSystemUiController()

                            when(backStackEntry?.destination?.route){
                                Screen.Splash.route -> {
                                    SideEffect {
                                        systemUiController.setStatusBarColor(
                                            color = Purple80
                                        )
                                    }
                                }
                                else -> {
                                    SideEffect {
                                        systemUiController.setStatusBarColor(
                                            color = Color.White
                                        )
                                    }
                                }
                            }
                        }
                    """.trimIndent()
                )

                KotlinCode(
                    code = """
                    @Composable
                    fun AnalyticsScreen(screenName: String, userId: String) {
                        val analytics = remember { AnalyticsProvider() }

                    // Use SideEffect to send the screen name to the analytics service after every recomposition
                        SideEffect {
                            analytics.logScreenView(screenName, userId)
                        }
                        // Screen content goes here
                        Column(
                            modifier = Modifier
                                .fillMaxSize()
                                .background(Color.White),
                            horizontalAlignment = Alignment.CenterHorizontally,
                            verticalArrangement = Arrangement.Center
                        ) {
                            Text(screenName, color = Color.Black, style = MaterialTheme.typography.h5)
                        }
                    }
                    class AnalyticsProvider {
                        fun logScreenView(screenName: String, userId: String) {
                            // Imagine this method sends the screen view event to an analytics service
                            println(screenName)
                        }
                    }

                    //Example2
                    @Composable
                    fun OuterComposable() {
                        var outerState by remember { mutableStateOf("Initial Outer State") }
                        var innerState by remember { mutableStateOf("Initial Inner State") }

                        // SideEffect in the outer composable
                        SideEffect {
                            println(outerState)
                        }

                        Column {
                            Text(outerState)
                            Button(onClick = { outerState = "Updated Outer State" }) {
                                Text("Update Outer State")
                            }

                            InnerComposable(innerState) {
                                innerState = "Updated Inner State"
                            }
                        }
                    }

                    @Composable
                    fun InnerComposable(innerState: String, onUpdateState: () -> Unit) {
                        // SideEffect in the inner composable
                        SideEffect {
                            println(innerState)
                        }

                        Column {
                            Text(innerState)
                            Button(onClick = onUpdateState) {
                                Text("Update Inner State")
                            }
                        }
                    }
                    """.trimIndent()
                )

                KotlinCode(
                    code = """
                        SideEffect {
                            Firebase.analytics.logEvent( "ScreenLoaded", null)
                        }
                    """.trimIndent()
                )
            }

            SpanText(
                text = "Type of effect — handlers (side effect)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Any Composable enters the composition when attached to the UI tree, and finally leaves the composition when detached from it. Between both events, effects might run. Some effects can outlive this cycle, so you can span an effect across recompositions.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "I thought we could divide effects in two categories for clarity:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Suspended effect handler: This effect-handler is for suspending functions: LaunchEffect, rememberCoroutineScope, Load data from network to feed some UI state.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Non-suspended effect handler: E.g: Run a side effect to initialize a callback when the Composable enters the composition, dispose it when it leaves. For example: DisposableEffect, SideEffect",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            SpanText(
                text = "remember(), rememberSaveable(), retain(), rememberSerializable()",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "remember() - lives in the composition. Survives recomposition, but dies on config change (rotation, dark mode switch).",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "rememberSaveable() — persists through config changes via Bundle. Limited to Parcelable/Saver-compatible types and has Bundle size constraints.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "retain() — survives recomposition and configuration changes, just like a ViewModel. No Parcelable needed. No Bundle limits.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "rememberSerializable() → survives config changes with ANY @Serializable data class",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "What Not To Do",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Don’t perform expensive operations inside SideEffect because it runs on each recomposition, which could lead to unnecessary overhead.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Avoid putting side effects that need to run once or after certain actions. Use LaunchedEffect for those cases.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "How does SideEffect enable communication with non-Compose code?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "SideEffect is used to publish Compose state to non-Compose code. It is invoked on every successful recomposition and can be used to communicate state changes to external libraries or systems that are not managed by Jetpack Compose.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }
        }
    }
}

//https://medium.com/@annakaimlettu/jetpack-compose-effects-interview-cheat-sheet-d65d4c9f72fc